import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import JoditEditor from 'jodit-react';
import { INSTRUCTION_BUILDER_TABS } from '../../../helpers/constants';
import { toast } from 'sonner';

const StepModal = ({ show, onHide, step, county, municipality, onSaveStep }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tab: '',
    scope: '',
    instruction: ''
  });
  const isEditing = Boolean(step);

  useEffect(() => {
    if (show) {
      if (step) {
        // Editing existing step
        setFormData({
          title: step.title || '',
          description: step.description || '',
          tab: step.tab || '',
          scope: step.scope || '',
          instruction: step.instruction || ''
        });
      } else {
        setFormData({
          title: '',
          description: '',
          tab: '',
          scope: '',
          instruction: ''
        });
      }
    }
  }, [show, step]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!formData.tab) {
      toast.error('Please select a tab');
      return;
    }

    if (!isEditing && !formData.scope) {
      toast.error('Please select County or Municipality');
      return;
    }

    onSaveStep(formData, step);
    onHide();
  };

  const editorConfig = useMemo(() => ({
    readonly: false,
    placeholder: 'Enter instruction content...',
    height: 400,
    toolbar: true,
    toolbarButtonSize: 'medium',
    spellcheck: true,
    language: 'en',
    toolbarAdaptive: false,
    toolbarSticky: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_as_html',
    disablePlugins: [],
    buttons: [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'superscript',
      'subscript',
      '|',
      'align',
      '|',
      'ul',
      'ol',
      '|',
      'outdent',
      'indent',
      '|',
      'font',
      'fontsize',
      'brush',
      'paragraph',
      '|',
      'image',
      'link',
      '|',
      'table',
      '|',
      'hr',
      'eraser',
      'copyformat',
      '|',
      'fullsize',
      'selectall',
      'print',
      '|',
      'undo',
      'redo',
      '|',
      'source',
      '|',
      'cut',
      'copy',
      'paste',
      'pasteAsText',
      '|',
      'about'
    ],
    controls: {
      table: {
        mergeCells: true,
        addColumn: true,
        addRow: true,
        deleteColumn: true,
        deleteRow: true,
        splitCells: true,
        setHeader: true,
        setFooter: true,
        setBody: true,
        resize: true
      }
    }
  }), [])

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Step' : 'Add New Step'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Title */}
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter step title"
              value={formData.title}
              onChange={e => handleChange('title', e.target.value)}
            />
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Enter step description"
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
            />
          </Form.Group>

          {/* Tab Selection */}
          <Form.Group className="mb-3">
            <Form.Label>
              Tab Category <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control as="select" value={formData.tab} onChange={e => handleChange('tab', e.target.value)} required>
              <option value="">Select a tab</option>
              {INSTRUCTION_BUILDER_TABS.map(tab => (
                <option key={tab} value={tab}>
                  {tab}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {/* Scope Selection (only for new steps) */}
          {!isEditing && (
            <Form.Group className="mb-3">
              <Form.Label>
                Scope <span className="text-danger">*</span>
              </Form.Label>
              <div className="row">
                {[
                  {
                    value: 'county',
                    label: 'County'
                  },
                  {
                    value: 'municipality',
                    label: 'Municipality'
                  }
                ].map(option => {
                  const isSelected = formData.scope === option.value;
                  return (
                    <div key={option.value} className="col-6">
                      <button
                        type="button"
                        onClick={() => handleChange('scope', option.value)}
                        className={`w-100 text-left border p-2 shadow-none pointer d-flex flex-column align-items-center justify-content-between ${
                          isSelected ? 'border-info bg-info-light' : 'bg-light'
                        }`}
                        style={{ transition: 'all 0.2s ease', borderRadius: '8px' }}
                      >
                        <div className={`font-weight-bold smallFont ${isSelected ? 'text-info' : 'text-muted'}`}>
                          {option.label}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </Form.Group>
          )}

          {/* Instruction Editor */}
          <Form.Group className="mb-3">
            <Form.Label>Instruction Content</Form.Label>
            <div style={{ minHeight: '300px', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
              <JoditEditor
                value={formData.instruction}
                config={editorConfig}
                tabIndex={1}
                onChange={newContent => handleChange('instruction', newContent)}
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="success" type="submit">
            {isEditing ? 'Update Step' : 'Add Step'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default StepModal;
